import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Soup, Coffee, CupSoda, Salad, Milk, GlassWater, Wheat, Flame, Croissant, Sandwich, UtensilsCrossed, Cookie, Pizza, Sparkles } from "lucide-react";
import Reveal from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";
import QuickView from "@/components/QuickView";
import { useState } from "react";

const ICONS = { Soup, Coffee, CupSoda, Salad, Milk, GlassWater, Wheat, Flame, Croissant, Sandwich, UtensilsCrossed, Cookie, Pizza };
const Icon = ({ name, className }) => {
  const C = ICONS[name] || Sparkles;
  return <C className={className} />;
};

const FILTERS = [
  { id: "all", label: "All 12" },
  { id: "flavour", label: "Flavour" },
  { id: "superfood", label: "Superfood" },
  { id: "immunity", label: "Immunity" },
  { id: "energy", label: "Energy" },
];

export function Squad({ products }) {
  const [tag, setTag] = useState("all");
  const [quick, setQuick] = useState(null);
  const filtered = tag === "all" ? products : products.filter((p) => p.tags.includes(tag));
  const shown = filtered.slice(0, 8);

  return (
    <section id="squad" className="mx-auto max-w-7xl px-4 py-24 sm:px-6" data-testid="squad-section">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-leaf">The full lineup</p>
            <h2 className="mt-2 font-display text-[7vw] font-extrabold leading-[1.1] tracking-tight text-forest sm:text-5xl">
              12 POWDERS. <span className="text-leaf">SO MANY POSSIBILITIES.</span>
            </h2>
          </div>
          <Link to="/shop" data-testid="squad-view-all-link" className="group flex items-center gap-2 rounded-full border-2 border-forest px-5 py-2.5 text-sm font-extrabold uppercase tracking-wide text-forest transition-all hover:bg-forest hover:text-cream">
            Shop all <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </Reveal>

      <Reveal delay={0.1} className="mt-8">
        <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar" data-testid="squad-filters">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              data-testid={`squad-filter-${f.id}`}
              onClick={() => setTag(f.id)}
              className={`shrink-0 rounded-full px-5 py-2.5 text-xs font-extrabold uppercase tracking-wider transition-all ${
                tag === f.id ? "bg-forest text-cream" : "border border-sand bg-white text-forest hover:border-leaf hover:text-leaf"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </Reveal>

      <div className="mt-8 grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-3 xl:grid-cols-4">
        {shown.map((p, i) => (
          <ProductCard key={p.slug} product={p} index={i} onQuickView={setQuick} />
        ))}
      </div>

      <QuickView product={quick} onClose={() => setQuick(null)} />
    </section>
  );
}

export function WhyBento() {
  return (
    <section id="story" className="relative bg-forest py-24 text-cream" data-testid="story-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Reveal>
          <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-leaf">Why powders, though?</p>
          <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tight sm:text-5xl">
            GOOD FOOD, LESS PREP.
          </h2>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-12">
          <Reveal className="md:col-span-7" delay={0}>
            <div className="flex h-full flex-col justify-between rounded-3xl bg-cream p-8 text-forest sm:p-10">
              <p className="font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-leaf">The math</p>
              <p className="mt-6 font-display text-5xl font-extrabold leading-[0.95] tracking-tight sm:text-6xl">
                1 SPOON
                <span className="mx-2 text-leaf">=</span>
                1 ONION
              </p>
              <p className="mt-6 max-w-md text-sm leading-relaxed text-ink/70 sm:text-base">
                Every pouch starts as one ingredient — sun-dried, milled fine, sealed tight. Same depth, same aroma, none of the knife work.
              </p>
            </div>
          </Reveal>

          <Reveal className="md:col-span-5" delay={0.08}>
            <div className="flex h-full flex-col justify-between overflow-hidden rounded-3xl bg-cream p-8 text-forest sm:p-10">
              <div className="flex items-start justify-between">
                <p className="font-display text-6xl font-extrabold text-leaf">0</p>
                <img src="/characters/onion.webp" alt="Mr. Onio" className="h-20 w-20 object-contain" />
              </div>
              <p className="mt-4 font-display text-2xl font-extrabold leading-tight">tears,<br />chopping boards<br />or peeling.</p>
              <p className="mt-3 font-hand text-xl text-ink/60">— Mr. Onio, probably</p>
            </div>
          </Reveal>

          <Reveal className="md:col-span-4" delay={0}>
            <div className="h-full rounded-3xl border border-cream/15 bg-forest p-8 sm:p-10">
              <p className="font-display text-5xl font-extrabold text-leaf">100%</p>
              <p className="mt-3 font-display text-xl font-extrabold">single ingredient.</p>
              <p className="mt-3 text-sm leading-relaxed text-cream/60">No fillers, no anti-caking agents, no "natural flavours". The label is one line long.</p>
            </div>
          </Reveal>

          <Reveal className="md:col-span-4" delay={0.08}>
            <div className="h-full rounded-3xl border border-cream/15 bg-forest p-8 sm:p-10">
              <p className="font-display text-5xl font-extrabold text-leaf">0</p>
              <p className="mt-3 font-display text-xl font-extrabold">fridge space used.</p>
              <p className="mt-3 text-sm leading-relaxed text-cream/60">Cool, dry cupboard, resealable pouch. It just waits there, ready, for months.</p>
            </div>
          </Reveal>

          <Reveal className="md:col-span-4" delay={0.16}>
            <div className="relative flex h-full flex-col justify-end overflow-hidden rounded-3xl bg-cream">
              <img src="/products/beetroot.webp" alt="Miss Beity Beetroot Powder packaging" className="absolute inset-0 h-full w-full object-cover" />
              <div className="relative z-10 bg-gradient-to-t from-black/70 via-black/10 to-transparent p-6 pt-20">
                <p className="font-display text-xl font-extrabold text-white">Packaging worth photographing</p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export function WaysToUse({ product }) {
  const ways = product ? product.uses : [
    { title: "1-Scoop Curry Thickener", desc: "Onion + tomato powder bloomed in hot oil. Gravy, sorted.", icon: "Soup" },
    { title: "Golden Immunity Latte", desc: "Turmeric + ginger in warm milk. 30 seconds to golden.", icon: "Coffee" },
    { title: "Ruby Detox Smoothie", desc: "Beetroot + carrot with banana. Pink never tasted this adult.", icon: "CupSoda" },
    { title: "Green Superfood Bowl", desc: "Moringa + curry leaves stirred into dal. Grandma-approved.", icon: "Salad" },
  ];

  return (
    <section id="ways" className="mx-auto max-w-7xl px-4 py-24 sm:px-6" data-testid="ways-to-use-section">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-leaf">Ten-second recipes</p>
            <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-forest sm:text-5xl">WAYS TO USE IT.</h2>
          </div>
          <p className="font-hand text-2xl text-forest/60">sprinkle. stir. serve.</p>
        </div>
      </Reveal>

      <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {ways.map((w, i) => (
          <Reveal key={w.title} delay={i * 0.07} className="h-full">
            <div
              data-testid={`ways-card-${i}`}
              className="group flex h-full flex-col rounded-3xl border border-sand bg-white p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-leaf/40 hover:shadow-xl hover:shadow-forest/10"
            >
              <div className="flex items-center justify-between">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-forest/5 text-forest transition-colors duration-300 group-hover:bg-leaf group-hover:text-white">
                  <Icon name={w.icon} className="h-5 w-5" />
                </div>
                <span className="rounded-full bg-leaf/10 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-leaf">under 1 min</span>
              </div>
              <h3 className="mt-6 font-display text-lg font-extrabold leading-snug text-forest">{w.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-ink/60">{w.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export function SocialBand({ products }) {
  const cast = [
    { slug: "ginger", quote: "your chai called" },
    { slug: "banana", quote: "blend me into everything" },
    { slug: "onion", quote: "no tears, only flavour" },
  ];
  const picks = cast.map((c) => ({ ...c, product: products.find((p) => p.slug === c.slug) })).filter((c) => c.product);

  return (
    <section className="relative overflow-hidden bg-forest py-24 text-cream" data-testid="social-section">
      <p className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap font-display text-[22vw] font-extrabold uppercase text-cream/[0.03]">
        POWDER POUR DONE
      </p>
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
        <Reveal>
          <h2 className="text-center font-display text-5xl font-extrabold leading-[1.02] tracking-tight sm:text-7xl">
            POWDER. <span className="text-leaf">POUR.</span> DONE.
          </h2>
          <p className="mx-auto mt-4 max-w-md text-center text-sm text-cream/60 sm:text-base">
            Your pantry just got smarter — and honestly, way more photogenic.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-3">
          {picks.map((c, i) => (
            <Reveal key={c.slug} delay={i * 0.1}>
              <Link to={`/product/${c.slug}`} data-testid={`social-tile-${c.slug}`} className="group block overflow-hidden rounded-3xl border border-cream/10 bg-cream/[0.04] transition-all duration-300 hover:-translate-y-1.5 hover:border-leaf/40">
                <div className="flex items-center gap-3 px-6 pt-5">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-leaf/20">
                    <span className="font-display text-xs font-extrabold text-leaf">a</span>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-cream">abhiveshafoods</p>
                    <p className="text-[10px] text-cream/50">Original audio · pantry beats</p>
                  </div>
                </div>
                <div className="relative mt-4 aspect-square overflow-hidden">
                  <img src={c.product.character} alt={`${c.product.mascot} mascot`} className="h-full w-full object-contain p-6 transition-transform duration-500 group-hover:scale-105" />
                  <p className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-forest/90 to-transparent p-5 pt-10 text-center font-hand text-2xl text-cream">"{c.quote}"</p>
                </div>
                <p className="px-6 py-4 text-[11px] font-semibold text-cream/50">
                  <span className="text-leaf">Meet {c.product.mascot}</span> — {c.product.tagline}
                </p>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function B2BTeaser() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6" data-testid="b2b-teaser">
      <Reveal>
        <div className="grain relative overflow-hidden rounded-[2rem] bg-forest px-6 py-16 text-center text-cream sm:px-12">
          <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-leaf/20 blur-3xl" />
          <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-leaf">For retailers &amp; distributors</p>
          <h2 className="mx-auto mt-4 max-w-3xl font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            STOCK THE SHELF EVERYONE PHOTOGRAPHS.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-sm text-cream/60 sm:text-base">
            12 SKUs. Long shelf life. Zero cold chain. Packaging that does the merchandising for you.
          </p>
          <Link to="/distribute" data-testid="b2b-teaser-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-leaf px-8 py-4 text-sm font-extrabold uppercase tracking-wide text-white transition-all hover:bg-cream hover:text-forest active:scale-95">
            Distribute with us <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Reveal>
    </section>
  );
}
